import get from 'lodash/get';
/*
 * This file contains selector functions for a variety of commonly used fields. The card object
 * to select on is expected to be in the format returned from the Offer API.
 *
 * Gotcha: the value of calculated rewards fields returned from the API is for the sum of the
 * entire time queried for (parameters.number_of_months). We commonly query for a time period of 2
 * years and want to display the average annual rewards rate so you may need to manipulate some
 * of the derived values to get the number you want to display
 */

export var getApplyLink = function getApplyLink(card) {
  return get(card, 'derived_details.apply_link.value');
};
export var getApprovalOdds = function getApprovalOdds(card) {
  return get(card, 'derived_details.approval_likelihood');
};
export var getAPRDisplayList = function getAPRDisplayList(card) {
  return get(card, 'details.apr_display_list.value');
};
export var getAPRIntroMessageList = function getAPRIntroMessageList(card) {
  return get(card, 'details.apr_intro_message_list.value');
};
export var getAnnualFee = function getAnnualFee(card) {
  return get(card, 'details.annual_fee.value');
};
export var getAnnualFeeDetails = function getAnnualFeeDetails(card) {
  return get(card, 'details.annual_fee_details.value');
};
export var getAnnualFeeFirstYear = function getAnnualFeeFirstYear(card) {
  return get(card, 'details.annual_fee_first_year.value');
};
export var getBalanceTransferFee = function getBalanceTransferFee(card) {
  return get(card, 'details.balance_transfer_fee.value');
};
export var getBalanceTransferFeeDescription = function getBalanceTransferFeeDescription(card) {
  return get(card, 'details.balance_transfer_fee_description.value');
};
export var getBalanceTransferInterestSavings = function getBalanceTransferInterestSavings(card) {
  return get(card, 'derived_details.balance_transfer_interest_savings.value');
};
export var getBalanceTransferIntroAPR = function getBalanceTransferIntroAPR(card) {
  return get(card, 'details.balance_transfer_intro_apr.value');
};
export var getBalanceTransferIntroFee = function getBalanceTransferIntroFee(card) {
  return get(card, 'details.balance_transfer_intro_fee.value');
};
export var getBalanceTransferIntroFeeDescription = function getBalanceTransferIntroFeeDescription(card) {
  return get(card, 'details.balance_transfer_intro_fee_description.value');
};
export var getBalanceTransferIntroTime = function getBalanceTransferIntroTime(card) {
  return get(card, 'details.balance_transfer_intro_time.value');
};
export var getBillingCycleUnits = function getBillingCycleUnits(card) {
  return get(card, 'details.billing_cycle_units.value');
};
export var getCardUser = function getCardUser(card) {
  return get(card, 'details.card_user.value');
};
export var getCategories = function getCategories(card) {
  return get(card, 'details.card_categories.value');
};
export var getCategorySpend = function getCategorySpend(card, category) {
  return get(card, "derived_details.category_".concat(category, ".value"), 0);
};
export var getCompareCategories = function getCompareCategories(card) {
  return get(card, 'details.compare_categories.value', []);
};
export var getConanicalUrl = function getConanicalUrl(card) {
  return get(card, 'details.canonical_url.value');
}; // TODO: these can contain HTML, so should we try to sanitize it here?

export var getConsBullets = function getConsBullets(card) {
  return get(card, 'details.cons_bullets.value');
};
export var getDisclaimer = function getDisclaimer(card) {
  return get(card, 'details.disclaimer.value');
};
export var getEstimatedOngoingAPR = function getEstimatedOngoingAPR(card) {
  return get(card, 'derived_details.estimated_ongoing_apr.value');
};
export var getFees = function getFees(card) {
  return get(card, 'derived_details.fees.value', 0);
};
export var getFicoApr = function getFicoApr(card, ficoBucket) {
  return get(card, "details.apr_".concat(ficoBucket, ".value"));
};
export var getForeignTransactionFee = function getForeignTransactionFee(card) {
  return get(card, 'details.foreign_transaction_fee.value');
};
export var getForeignTransactionFeeType = function getForeignTransactionFeeType(card) {
  return get(card, 'details.foreign_transaction_fee.type');
};
export var getForeignTransactionFeeDescription = function getForeignTransactionFeeDescription(card) {
  return get(card, 'details.foreign_transaction_fee_description.value');
};
export var getCardId = function getCardId(card) {
  return get(card, 'id');
};
export var getImage = function getImage(card, size) {
  if (size) {
    return get(card, "details.image_source_".concat(size, ".value"));
  }

  return get(card, 'details.image_source.value');
};
export var getImageAltText = function getImageAltText(card) {
  return get(card, 'details.image_alt_text.value');
};
export var getImageIsPortrait = function getImageIsPortrait(card) {
  return get(card, 'details.image_is_portrait.value');
};
export var getIsFixedRate = function getIsFixedRate(card) {
  return get(card, 'details.is_fixed_rate.value');
};
export var getIsMonetizing = function getIsMonetizing(card) {
  return get(card, 'details.is_monetizing.value');
};
export var getIssuerId = function getIssuerId(card) {
  return get(card, 'product.institution.id');
};
export var getIssuerName = function getIssuerName(card) {
  return get(card, 'product.institution.name');
};
export var getLegacyId = function getLegacyId(card) {
  return get(card, 'details.legacy_id.value');
};
export var getMarketingBullets = function getMarketingBullets(card) {
  return get(card, 'details.marketing_bullets_without_terms_and_fees_link.value') || get(card, 'details.marketing_bullets.value', []);
};
export var getMaximumAPR = function getMaximumAPR(card) {
  return get(card, 'details.apr_max.value');
};
export var getMaximumFICO = function getMaximumFICO(card) {
  return get(card, 'details.max_fico.value');
};
export var getMetaKeywords = function getMetaKeywords(card) {
  return get(card, 'details.meta_keywords.value', []);
};
export var getMetaTitle = function getMetaTitle(card) {
  return get(card, 'details.meta_title.value');
};
export var getMinimumAPR = function getMinimumAPR(card) {
  return get(card, 'details.apr_min.value');
};
export var getMinimumDeposit = function getMinimumDeposit(card) {
  return get(card, 'details.minimum_deposit.value');
};
export var getMinimumDepositDescription = function getMinimumDepositDescription(card) {
  return get(card, 'details.minimum_deposit_description.value');
};
export var getMinimumFICO = function getMinimumFICO(card) {
  return get(card, 'details.min_fico.value');
};
export var getName = function getName(card) {
  return get(card, 'name');
};
export var getNetRewards = function getNetRewards(card) {
  return get(card, 'derived_details.net_rewards.value', 0);
};
export var getObsoleteReferrals = function getObsoleteReferrals(card) {
  return get(card, 'details.obsolete_referrals.value', []);
};
export var getOngoingAPR = function getOngoingAPR(card) {
  return get(card, 'details.ongoing_apr.value');
};
export var getOngoingAPRAnnotated = function getOngoingAPRAnnotated(card) {
  return get(card, 'details.ongoing_apr_annotated.value');
};
export var getOngoingAPRUnannotated = function getOngoingAPRUnannotated(card) {
  return get(card, 'details.ongoing_apr_unannotated.value');
};
export var getPayPerCall = function getPayPerCall(card) {
  return get(card, 'details.apply_pay_per_call.value');
};
export var getPrequalLink = function getPrequalLink(card) {
  return get(card, 'details.prequal_link.value');
};
export var getPrequalLinkText = function getPrequalLinkText(card) {
  return get(card, 'details.prequal_link_text.value');
};
export var getProductId = function getProductId(card) {
  return get(card, 'product.id');
}; // TODO: these can contain HTML, so should we try to sanitize it here?

export var getProsBullets = function getProsBullets(card) {
  return get(card, 'details.pros_bullets.value');
};
export var getProsConsType = function getProsConsType(card) {
  return get(card, 'details.pros_bullets.type');
};
export var getPurchaseIntroAPR = function getPurchaseIntroAPR(card) {
  return get(card, 'details.purchase_intro_apr.value');
};
export var getPurchaseIntroTime = function getPurchaseIntroTime(card) {
  return get(card, 'details.purchase_intro_time.value');
};
export var getPurchaseInterestSavings = function getPurchaseInterestSavings(card) {
  return get(card, 'derived_details.purchase_interest_savings.value');
};
export var getRating = function getRating(card) {
  return get(card, 'details.rating_stars.value');
};
export var getRewardsRate = function getRewardsRate(card) {
  return get(card, 'details.reward_rate.value');
};
export var getRewardsRateDetails = function getRewardsRateDetails(card) {
  return get(card, 'details.reward_rate_details.value');
};
export var getRewardsRateNotes = function getRewardsRateNotes(card) {
  return get(card, 'details.reward_rate_notes.value');
};
export var getRewardsType = function getRewardsType(card) {
  return get(card, 'details.rewards_type.value');
};
export var getSignUpBonus = function getSignUpBonus(card) {
  return get(card, 'derived_details.sign_up_bonus.value', 0);
};
/**
 * Reads the "maximum" sign-up bonus (from "details" instead of "derived_details").
 * WARNING: For some Discover cards (e.g. Discover It), they match cashback for spend during the
 * first year, so this will return 0 while `getSignUpBonus()` will return an amount derived from
 * assumed/calculated spend
 *
 * @param {Object} card - the card whose sign-up bonus we want to extract
 * @returns {Number} the card's sign-up bonus
 */

export var getSignUpBonusMax = function getSignUpBonusMax(card) {
  return get(card, 'details.sign_up_bonus.value', 0);
};
export var getSignUpBonusDetails = function getSignUpBonusDetails(card) {
  return get(card, 'details.sign_up_bonus_details.value');
};
export var getSignUpBonusDetailsAnnotation = function getSignUpBonusDetailsAnnotation(card) {
  return get(card, 'details.sign_up_bonus_details_annotation.value');
};
export var getSpendingRewards = function getSpendingRewards(card) {
  return get(card, 'derived_details.spending_rewards.value', 0);
};
export var getSlug = function getSlug(card) {
  return get(card, 'details.slug.value');
};
export var getStatus = function getStatus(card) {
  return get(card, 'status');
};
export var getTermsAndFeesLink = function getTermsAndFeesLink(card) {
  return get(card, 'derived_details.terms_and_fees_redirect_link.value', get(card, 'details.terms_and_fees_link.value'));
};
export var getTotalSavingsAmount = function getTotalSavingsAmount(card) {
  return get(card, 'derived_details.total_savings_amount.value');
};
export var getTrueBalanceTranferCost = function getTrueBalanceTranferCost(card) {
  return get(card, 'derived_details.true_balance_transfer_cost.value');
};
export var getTrueInterestCost = function getTrueInterestCost(card) {
  return get(card, 'derived_details.true_interest_cost.value');
};
export var getTotalBalanceTransferSavingsAmount = function getTotalBalanceTransferSavingsAmount(card) {
  return get(card, 'derived_details.total_savings_amount.value');
};
export var getTotalBalanceTransferSavingsTime = function getTotalBalanceTransferSavingsTime(card) {
  return get(card, 'derived_details.total_balance_transfer_savings_time.value');
};
export var getTotalBalanceTransferSavingsOngoingInterest = function getTotalBalanceTransferSavingsOngoingInterest(card) {
  return get(card, 'derived_details.total_balance_transfer_savings_ongoing_interest.value');
};
export var getTotalPurchaseSavingsAmount = function getTotalPurchaseSavingsAmount(card) {
  return get(card, 'derived_details.total_purchase_savings_amount.value');
};
export var getTotalPurchaseSavingsTime = function getTotalPurchaseSavingsTime(card) {
  return get(card, 'derived_details.total_purchase_savings_time.value');
};
export var getTotalPurchaseSavingsOngoingInterest = function getTotalPurchaseSavingsOngoingInterest(card) {
  return get(card, 'derived_details.total_purchase_savings_ongoing_interest.value');
};
export var getVersionId = function getVersionId(card) {
  return get(card, 'version_id');
};